const DisclaimerModal = ({ onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Términos y Condiciones de Uso</h2>
                <p className="text-sm text-gray-500">Descargo de Responsabilidad - Doctor Vet AI</p>
              </div>
            </div>
            {/* Botón cerrar */}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Cerrar"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4 text-sm text-gray-700 leading-relaxed">
          <div className="space-y-6">
            {/* Introducción */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">AVISO LEGAL IMPORTANTE</h3>
              <p className="mb-3">
                <strong>POR FAVOR, LEA DETENIDAMENTE ESTOS TÉRMINOS Y CONDICIONES ANTES DE UTILIZAR ESTE SERVICIO.</strong> Al acceder, navegar o utilizar este asistente de inteligencia artificial ("Doctor Vet AI", "el Asistente", "el Servicio", "la Herramienta" o "el Chatbot"), usted ("el Usuario", "usted" o "su") reconoce que ha leído, comprendido y acepta quedar vinculado por estos Términos y Condiciones de Uso, así como por todas las leyes y regulaciones aplicables. Si no está de acuerdo con alguno de estos términos, no utilice este servicio.
              </p>
              <p>
                Este documento constituye un acuerdo legalmente vinculante entre usted y los desarrolladores, propietarios, operadores, licenciantes y afiliados de Doctor Vet AI (colectivamente, "nosotros", "nuestro" o "el Desarrollador").
              </p>
            </section>

            {/* Sección 1 */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">1. NATURALEZA DEL SERVICIO Y LIMITACIONES FUNDAMENTALES</h3>
              
              <h4 className="font-semibold text-gray-800 mt-4 mb-2">1.1 Carácter Exclusivamente Informativo</h4>
              <p className="mb-3">
                Doctor Vet AI es una herramienta de inteligencia artificial diseñada única y exclusivamente con fines informativos, educativos y de orientación general. La información proporcionada por este Asistente NO constituye, bajo ninguna circunstancia:
              </p>
              <ul className="list-disc list-inside mb-3 space-y-1 ml-4">
                <li>Asesoramiento médico veterinario profesional</li>
                <li>Diagnóstico clínico de enfermedades o condiciones en animales</li>
                <li>Prescripción de tratamientos, medicamentos o terapias</li>
                <li>Sustitución de la consulta con un médico veterinario colegiado y habilitado</li>
                <li>Opinión profesional vinculante sobre la salud animal</li>
                <li>Asesoría legal, financiera, contable o de gestión empresarial profesional</li>
                <li>Recomendación específica para la toma de decisiones críticas</li>
              </ul>

              <h4 className="font-semibold text-gray-800 mt-4 mb-2">1.2 Tecnología de Inteligencia Artificial</h4>
              <p className="mb-3">
                El Usuario reconoce y comprende que Doctor Vet AI opera mediante tecnología de inteligencia artificial basada en modelos de lenguaje de gran escala (Large Language Models - LLMs). Por su naturaleza tecnológica inherente, estos sistemas:
              </p>
              <ul className="list-disc list-inside mb-3 space-y-1 ml-4">
                <li>Generan respuestas basadas en patrones estadísticos y probabilísticos, no en razonamiento clínico profesional</li>
                <li>Pueden producir información inexacta, incompleta, desactualizada o completamente errónea (fenómeno conocido como "alucinaciones")</li>
                <li>No tienen la capacidad de examinar físicamente a ningún animal</li>
                <li>No pueden interpretar estudios de laboratorio, imágenes diagnósticas u otras pruebas complementarias de manera confiable</li>
                <li>Carecen de licencia, certificación o habilitación profesional para ejercer la medicina veterinaria</li>
                <li>No tienen acceso al historial médico real de ningún paciente animal</li>
                <li>No pueden evaluar el contexto completo de cada situación particular</li>
                <li>Pueden malinterpretar la información proporcionada por el Usuario</li>
                <li>Operan con conocimientos que pueden estar desactualizados</li>
              </ul>

              <h4 className="font-semibold text-gray-800 mt-4 mb-2">1.3 Obligación de Consultar Profesionales</h4>
              <p>
                <strong>EL USUARIO DEBE SIEMPRE, SIN EXCEPCIÓN, CONSULTAR A UN MÉDICO VETERINARIO COLEGIADO, HABILITADO Y DEBIDAMENTE CALIFICADO</strong> antes de tomar cualquier decisión relacionada con la salud, el bienestar, la alimentación, el tratamiento o el cuidado de cualquier animal. Las respuestas de este Asistente NUNCA deben utilizarse como base única o principal para decisiones que afecten la vida, la salud o el bienestar de animales.
              </p>
            </section>

            {/* Sección 2 */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">2. EXCLUSIÓN TOTAL Y ABSOLUTA DE RESPONSABILIDAD</h3>
              
              <h4 className="font-semibold text-gray-800 mt-4 mb-2">2.1 Exención General</h4>
              <p className="mb-3">
                En la máxima medida permitida por la ley aplicable, el Desarrollador, sus directores, empleados, agentes, colaboradores, socios, licenciantes, proveedores de servicios, contratistas, subcontratistas y cualquier otra parte relacionada con el desarrollo, mantenimiento u operación de Doctor Vet AI (colectivamente, las "Partes Exentas") quedan COMPLETA, TOTAL, ABSOLUTA E IRREVOCABLEMENTE EXENTAS de cualquier responsabilidad, obligación, reclamación, demanda, daño, perjuicio, pérdida, costo, gasto, multa, sanción o consecuencia de cualquier naturaleza que pudiera surgir, directa o indirectamente, del uso, la imposibilidad de uso, el mal uso, la confianza depositada en, o cualquier acción u omisión relacionada con este Servicio.
              </p>

              <h4 className="font-semibold text-gray-800 mt-4 mb-2">2.2 Categorías de Daños Excluidos</h4>
              <p className="mb-3">Sin limitar la generalidad de lo anterior, las Partes Exentas no serán responsables bajo ninguna circunstancia por:</p>
              <ul className="list-disc list-inside mb-3 space-y-1 ml-4">
                <li><strong>Daños a animales:</strong> Incluyendo pero no limitado a muerte, enfermedad, lesión, sufrimiento, deterioro de salud, agravamiento de condiciones preexistentes, reacciones adversas, efectos secundarios, complicaciones o cualquier otro daño físico, fisiológico o psicológico que pudiera sufrir cualquier animal.</li>
                <li><strong>Daños económicos:</strong> Pérdidas financieras, costos de tratamiento veterinario, gastos de hospitalización, medicamentos, cirugías, rehabilitación, lucro cesante, pérdida de ingresos, pérdida de oportunidades de negocio, daño emergente o cualquier otro perjuicio patrimonial.</li>
                <li><strong>Daños morales y emocionales:</strong> Sufrimiento emocional, angustia, estrés, ansiedad, depresión, trauma psicológico u otros daños no patrimoniales experimentados por propietarios, cuidadores o cualquier persona relacionada con animales afectados.</li>
                <li><strong>Daños indirectos y consecuenciales:</strong> Cualquier daño incidental, especial, punitivo, ejemplar, agravado o consecuencial, independientemente de su previsibilidad.</li>
                <li><strong>Daños a terceros:</strong> Cualquier daño, perjuicio o responsabilidad que el Usuario pudiera causar a terceras personas, animales o propiedades como resultado del uso de este Servicio.</li>
                <li><strong>Daños derivados de decisiones empresariales:</strong> Pérdidas relacionadas con decisiones de gestión, administración, inversión o estrategia comercial en clínicas veterinarias, tiendas de mascotas u otros negocios relacionados con animales.</li>
              </ul>

              <h4 className="font-semibold text-gray-800 mt-4 mb-2">2.3 Causas de Daño Excluidas</h4>
              <p className="mb-3">La exención de responsabilidad aplica independientemente de que los daños sean causados por:</p>
              <ul className="list-disc list-inside mb-3 space-y-1 ml-4">
                <li>Información incorrecta, inexacta, incompleta o engañosa proporcionada por el Asistente</li>
                <li>Errores, omisiones, bugs, fallos técnicos o mal funcionamiento del sistema</li>
                <li>Interpretación errónea de las preguntas o datos proporcionados por el Usuario</li>
                <li>Retrasos en el diagnóstico o tratamiento debido a la confianza en el Asistente</li>
                <li>Interrupción, suspensión o discontinuación del Servicio</li>
                <li>Acceso no autorizado, hackeo, manipulación o corrupción de datos</li>
                <li>Virus, malware u otros programas dañinos</li>
                <li>Incompatibilidad con dispositivos, navegadores o sistemas del Usuario</li>
                <li>Cualquier otra causa, sea conocida o desconocida, prevista o imprevista</li>
              </ul>
            </section>

            {/* Sección 3 */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">3. RENUNCIA Y LIBERACIÓN DE RECLAMACIONES</h3>
              
              <h4 className="font-semibold text-gray-800 mt-4 mb-2">3.1 Renuncia Expresa</h4>
              <p className="mb-3">
                Al utilizar este Servicio, el Usuario RENUNCIA EXPRESAMENTE, de manera consciente, voluntaria, informada e irrevocable, a cualquier derecho, acción, recurso o reclamación que pudiera tener o adquirir en el futuro contra las Partes Exentas en relación con:
              </p>
              <ul className="list-disc list-inside mb-3 space-y-1 ml-4">
                <li>La calidad, precisión, utilidad o idoneidad de las respuestas generadas</li>
                <li>Los resultados obtenidos o no obtenidos mediante el uso del Servicio</li>
                <li>Las consecuencias de actuar o no actuar basándose en la información proporcionada</li>
                <li>Cualquier expectativa, representación o garantía implícita o explícita</li>
              </ul>

              <h4 className="font-semibold text-gray-800 mt-4 mb-2">3.2 Liberación de Responsabilidad</h4>
              <p className="mb-3">
                El Usuario libera y descarga a las Partes Exentas de toda responsabilidad pasada, presente y futura relacionada con el Servicio. Esta liberación es completa, definitiva e incondicional, y permanecerá vigente incluso después de la terminación del uso del Servicio.
              </p>

              <h4 className="font-semibold text-gray-800 mt-4 mb-2">3.3 Indemnización</h4>
              <p>
                El Usuario se compromete a indemnizar, defender y mantener indemne a las Partes Exentas frente a cualquier reclamación, demanda, acción legal, responsabilidad, daño, pérdida, costo o gasto (incluyendo honorarios legales razonables) que surja de o esté relacionado con: (a) el uso del Servicio por parte del Usuario; (b) la violación de estos Términos; (c) la violación de derechos de terceros; (d) cualquier contenido o información proporcionada por el Usuario.
              </p>
            </section>

            {/* Sección 4 */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">4. AUSENCIA DE GARANTÍAS</h3>
              
              <h4 className="font-semibold text-gray-800 mt-4 mb-2">4.1 Servicio "TAL CUAL"</h4>
              <p className="mb-3">
                El Servicio se proporciona estrictamente "TAL CUAL" ("AS IS"), "SEGÚN DISPONIBILIDAD" ("AS AVAILABLE") y "CON TODOS SUS DEFECTOS" ("WITH ALL FAULTS"), sin garantía de ningún tipo, ya sea expresa, implícita, estatutaria o de otro tipo.
              </p>

              <h4 className="font-semibold text-gray-800 mt-4 mb-2">4.2 Garantías Expresamente Excluidas</h4>
              <p className="mb-3">Las Partes Exentas rechazan expresamente cualquier garantía, incluyendo pero no limitado a:</p>
              <ul className="list-disc list-inside mb-3 space-y-1 ml-4">
                <li>Garantías implícitas de comerciabilidad o aptitud comercial</li>
                <li>Garantías de idoneidad para un propósito particular</li>
                <li>Garantías de no infracción de derechos de propiedad intelectual</li>
                <li>Garantías de precisión, confiabilidad, actualidad o integridad de la información</li>
                <li>Garantías de disponibilidad continua e ininterrumpida del Servicio</li>
                <li>Garantías de que el Servicio estará libre de errores o defectos</li>
                <li>Garantías de que los resultados serán satisfactorios o cumplirán las expectativas del Usuario</li>
                <li>Garantías de seguridad o protección contra virus y amenazas informáticas</li>
                <li>Cualquier otra garantía que pudiera surgir del curso de las negociaciones, el uso o la práctica comercial</li>
              </ul>
            </section>

            {/* Sección 5 */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">5. USO BAJO PROPIO RIESGO</h3>
              
              <p className="mb-3">
                <strong>EL USUARIO UTILIZA ESTE SERVICIO ENTERAMENTE BAJO SU PROPIO RIESGO Y RESPONSABILIDAD.</strong> El Usuario reconoce y acepta que:
              </p>
              <ul className="list-disc list-inside mb-3 space-y-1 ml-4">
                <li>Ha sido debidamente advertido sobre las limitaciones y riesgos del Servicio</li>
                <li>Comprende plenamente que la información proporcionada puede ser incorrecta o perjudicial</li>
                <li>Asume voluntariamente todos los riesgos asociados con el uso del Servicio</li>
                <li>Es el único responsable de evaluar la pertinencia y aplicabilidad de cualquier información recibida</li>
                <li>No actuará basándose exclusivamente en las respuestas del Asistente sin verificación profesional independiente</li>
                <li>Cualquier consecuencia negativa derivada del uso del Servicio será de su exclusiva responsabilidad</li>
              </ul>
            </section>

            {/* Sección 6 */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">6. SITUACIONES DE EMERGENCIA</h3>
              
              <p className="mb-3">
                <strong>DOCTOR VET AI NO ES UN SERVICIO DE EMERGENCIAS.</strong> En caso de cualquier emergencia médica veterinaria, incluyendo pero no limitado a:
              </p>
              <ul className="list-disc list-inside mb-3 space-y-1 ml-4">
                <li>Dificultad respiratoria o asfixia</li>
                <li>Hemorragias severas o incontrolables</li>
                <li>Traumatismos, atropellos o caídas graves</li>
                <li>Convulsiones o pérdida de consciencia</li>
                <li>Ingestión de sustancias tóxicas o venenos</li>
                <li>Partos distócicos o complicados</li>
                <li>Obstrucciones intestinales o urinarias</li>
                <li>Golpes de calor o hipotermia severa</li>
                <li>Cualquier otra situación que comprometa la vida del animal</li>
              </ul>
              <p className="font-semibold text-red-700">
                EL USUARIO DEBE ACUDIR INMEDIATAMENTE A UN CENTRO VETERINARIO DE URGENCIAS O CONTACTAR A UN MÉDICO VETERINARIO DE EMERGENCIA. NO UTILICE ESTE SERVICIO EN SITUACIONES DE EMERGENCIA. CADA SEGUNDO CUENTA Y PUEDE SIGNIFICAR LA DIFERENCIA ENTRE LA VIDA Y LA MUERTE DEL ANIMAL.
              </p>
            </section>

            {/* Sección 7 */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">7. LIMITACIONES DE RESPONSABILIDAD</h3>
              
              <h4 className="font-semibold text-gray-800 mt-4 mb-2">7.1 Límite Máximo de Responsabilidad</h4>
              <p className="mb-3">
                En aquellas jurisdicciones que no permitan la exclusión total de responsabilidad, la responsabilidad agregada máxima de las Partes Exentas por cualquier concepto relacionado con el Servicio estará limitada a la cantidad de UN DÓLAR ESTADOUNIDENSE (USD $1.00), independientemente de la teoría legal invocada o del tipo de daño reclamado.
              </p>

              <h4 className="font-semibold text-gray-800 mt-4 mb-2">7.2 Exclusión de Daños Especiales</h4>
              <p>
                En ningún caso las Partes Exentas serán responsables por daños punitivos, ejemplares, especiales, incidentales, consecuenciales o indirectos, incluyendo pérdida de beneficios, pérdida de datos, pérdida de fondo de comercio, interrupción de negocio u otros daños intangibles, incluso si han sido advertidas de la posibilidad de tales daños.
              </p>
            </section>

            {/* Sección 8 */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">8. PROPIEDAD INTELECTUAL Y CONFIDENCIALIDAD</h3>
              
              <h4 className="font-semibold text-gray-800 mt-4 mb-2">8.1 Derechos de Propiedad</h4>
              <p className="mb-3">
                Todos los derechos de propiedad intelectual relacionados con el Servicio, incluyendo pero no limitado a software, código, algoritmos, diseño, interfaz, marca, logo, nombre comercial y contenido, son propiedad exclusiva del Desarrollador o sus licenciantes. El uso del Servicio no confiere al Usuario ningún derecho de propiedad sobre estos elementos.
              </p>

              <h4 className="font-semibold text-gray-800 mt-4 mb-2">8.2 Información del Usuario</h4>
              <p className="mb-3">
                El Usuario reconoce que las conversaciones con el Asistente pueden ser almacenadas, procesadas y utilizadas para mejorar el Servicio. El Usuario no debe proporcionar información confidencial, sensible, personal identificable o legalmente protegida a través del Servicio. El Desarrollador no garantiza la confidencialidad de la información proporcionada por el Usuario.
              </p>
            </section>

            {/* Sección 9 */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">9. MODIFICACIONES Y TERMINACIÓN</h3>
              
              <h4 className="font-semibold text-gray-800 mt-4 mb-2">9.1 Modificaciones</h4>
              <p className="mb-3">
                El Desarrollador se reserva el derecho de modificar, actualizar, suspender o discontinuar el Servicio, así como estos Términos y Condiciones, en cualquier momento y sin previo aviso. El uso continuado del Servicio después de cualquier modificación constituye la aceptación de los nuevos términos.
              </p>

              <h4 className="font-semibold text-gray-800 mt-4 mb-2">9.2 Terminación</h4>
              <p>
                El Desarrollador puede terminar o suspender el acceso del Usuario al Servicio en cualquier momento, por cualquier motivo o sin motivo, sin previo aviso y sin responsabilidad alguna. Las disposiciones de estos Términos que por su naturaleza deban sobrevivir a la terminación continuarán vigentes.
              </p>
            </section>

            {/* Sección 10 */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">10. DISPOSICIONES GENERALES</h3>
              
              <h4 className="font-semibold text-gray-800 mt-4 mb-2">10.1 Ley Aplicable y Jurisdicción</h4>
              <p className="mb-3">
                Estos Términos se regirán e interpretarán de acuerdo con las leyes del país de domicilio del Desarrollador, sin dar efecto a principios de conflicto de leyes. Cualquier disputa será sometida a la jurisdicción exclusiva de los tribunales competentes de dicha jurisdicción.
              </p>

              <h4 className="font-semibold text-gray-800 mt-4 mb-2">10.2 Divisibilidad</h4>
              <p className="mb-3">
                Si alguna disposición de estos Términos es declarada inválida, ilegal o inaplicable, las disposiciones restantes continuarán en pleno vigor y efecto. La disposición inválida será modificada en la medida mínima necesaria para hacerla válida y aplicable.
              </p>

              <h4 className="font-semibold text-gray-800 mt-4 mb-2">10.3 Renuncia</h4>
              <p className="mb-3">
                La falta de ejercicio o el retraso en ejercer cualquier derecho bajo estos Términos no constituirá una renuncia a dicho derecho. Ninguna renuncia será efectiva a menos que sea por escrito y firmada por la parte renunciante.
              </p>

              <h4 className="font-semibold text-gray-800 mt-4 mb-2">10.4 Acuerdo Completo</h4>
              <p className="mb-3">
                Estos Términos constituyen el acuerdo completo entre el Usuario y el Desarrollador con respecto al Servicio y reemplazan todos los acuerdos, comunicaciones y propuestas anteriores o contemporáneas, ya sean orales o escritas.
              </p>

              <h4 className="font-semibold text-gray-800 mt-4 mb-2">10.5 Cesión</h4>
              <p className="mb-3">
                El Usuario no puede ceder o transferir estos Términos ni sus derechos u obligaciones bajo los mismos sin el consentimiento previo por escrito del Desarrollador. El Desarrollador puede ceder libremente estos Términos sin restricción.
              </p>

              <h4 className="font-semibold text-gray-800 mt-4 mb-2">10.6 Idioma</h4>
              <p>
                En caso de discrepancia entre versiones de estos Términos en diferentes idiomas, prevalecerá la versión en español.
              </p>
            </section>

            {/* Sección 11 */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">11. DECLARACIONES Y RECONOCIMIENTOS DEL USUARIO</h3>
              
              <p className="mb-3">Al hacer clic en "Acepto" o al utilizar el Servicio, el Usuario declara y garantiza que:</p>
              <ul className="list-disc list-inside mb-3 space-y-2 ml-4">
                <li>Es mayor de edad según las leyes de su jurisdicción</li>
                <li>Tiene la capacidad legal para celebrar contratos vinculantes</li>
                <li>Ha leído, comprendido y acepta íntegramente estos Términos y Condiciones</li>
                <li>Comprende que Doctor Vet AI NO es un servicio médico veterinario profesional</li>
                <li>Comprende que las respuestas pueden contener errores graves y potencialmente peligrosos</li>
                <li>Se compromete a consultar siempre a un veterinario profesional para cualquier decisión sobre salud animal</li>
                <li>Asume voluntaria y conscientemente todos los riesgos asociados al uso del Servicio</li>
                <li>Renuncia expresamente a cualquier reclamación contra las Partes Exentas</li>
                <li>Se compromete a no utilizar el Servicio en situaciones de emergencia médica veterinaria</li>
                <li>No utilizará el Servicio para fines ilegales, fraudulentos o perjudiciales</li>
              </ul>
            </section>

            {/* Advertencia final */}
            <section className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="text-lg font-bold text-red-800 mb-3">ADVERTENCIA FINAL</h3>
              <p className="text-red-700 font-medium">
                LA SALUD Y LA VIDA DE LOS ANIMALES SON INVALUABLES. NINGUNA HERRAMIENTA DE INTELIGENCIA ARTIFICIAL PUEDE NI DEBE REEMPLAZAR EL CRITERIO, LA EXPERIENCIA Y EL EXAMEN CLÍNICO DE UN MÉDICO VETERINARIO PROFESIONAL. AL UTILIZAR ESTE SERVICIO, USTED ACEPTA QUE LO HACE BAJO SU PROPIO RIESGO Y QUE EL DESARROLLADOR NO TIENE NINGUNA RESPONSABILIDAD POR LAS CONSECUENCIAS DE DICHO USO.
              </p>
            </section>

            {/* Fecha de última actualización */}
            <section className="text-center text-gray-500 text-xs border-t pt-4">
              <p>Última actualización: Enero 2026</p>
              <p>Versión: 1.0</p>
            </section>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex-shrink-0">
          <p className="text-xs text-gray-500 text-center mb-3">
            Al utilizar Doctor Vet IA, usted acepta automáticamente estos términos y condiciones.
          </p>
          <button
            onClick={onClose}
            className="w-full py-3 px-4 rounded-xl font-semibold transition-all duration-200 bg-gray-800 text-white hover:bg-gray-700"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  )
}

export default DisclaimerModal
